import {
  Avatar,
  Menu,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  notify,
  Tooltip,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';

import * as styles from './member-item.css';
import type { TeamOrUser } from './member-management';
import { FolderRole } from '@affine/core/modules/db/schema/schema';
import { OrganizeService } from '@affine/core/modules/organize';

export const MemberItem = ({
  grantedUser,
  canManageUsers,
  nodeId,
}: {
  grantedUser: TeamOrUser;
  canManageUsers: boolean;
  nodeId: string;
}) => {
  const disableManage = grantedUser.role === FolderRole.Admin && !canManageUsers;

  const role = useMemo(() => {
    switch (grantedUser.role) {
      case FolderRole.Admin:
        return '可管理';
      case FolderRole.Write:
        return '可编辑';
      case FolderRole.Read:
        return '可查看';
      default:
        return '';
    }
  }, [grantedUser.role]);

  return (
    <div className={styles.memberItemStyle}>
      <div className={styles.memberContainerStyle}>
        <Avatar
          rounded={grantedUser.type === 'team' ? '4px' : '50%'}
          key={grantedUser.id}
          url={grantedUser?.avatar || ''}
          name={grantedUser?.name || ''}
          size={36}
        />
        <div className={styles.memberInfoStyle}>
          <Tooltip
            content={grantedUser?.name || ''}
            rootOptions={{ delayDuration: 1000 }}
            options={{
              className: styles.tooltipContentStyle,
            }}
          >
            <div className={styles.memberNameStyle}>{grantedUser?.name || ''}</div>
          </Tooltip>
        </div>
      </div>
      {disableManage ? (
        <div className={clsx(styles.memberRoleStyle, 'disable')}>{role}</div>
      ) : (
        <Menu
          items={
            <Options
              nodeId={nodeId}
              id={grantedUser.id}
              type={grantedUser.type}
              memberRole={grantedUser.role}
            />
          }
          contentOptions={{
            align: 'start',
          }}
        >
          <MenuTrigger
            variant="plain"
            className={styles.menuTriggerStyle}
            contentStyle={{
              width: '100%',
            }}
          >
            {role}
          </MenuTrigger>
        </Menu>
      )}
    </div>
  );
};

const Options = ({
  memberRole,
  id,
  type,
  nodeId,
}: {
  id: string;
  type: 'user' | 'team';
  memberRole: FolderRole;
  nodeId: string;
}) => {
  const t = useI18n();
  const workspacePermissionService = useService(WorkspacePermissionService);
  const organizeService = useService(OrganizeService);
  const node = useLiveData(
    organizeService.folderTree.folderNode$(nodeId)
  );
  const isWorkspaceOwner = useLiveData(
    workspacePermissionService.permission.isOwner$
  );
  
  const isOwner = useLiveData(node?.isOwner$);
  const isAdmin = useLiveData(node?.isManager$);

  const canDelete = useMemo(() => {
    return isWorkspaceOwner || isOwner || isAdmin;
  }, [isWorkspaceOwner, isOwner, isAdmin]);

  const updateUserRole = useCallback(
    async (userId: string,  role: FolderRole) => {
      try {
        node?.addPermission([
          {
            id: userId,
            type,
            role,
          },
        ])

      } catch (error) {
        const err = UserFriendlyError.fromAny(error);
        notify.error({
          title: t[`error.${err.name}`](err.data),
        });
      }
    },
    [ t]
  );

  const changeToManager = useAsyncCallback(async () => {
    await updateUserRole(id, FolderRole.Admin);
  }, [updateUserRole, id]);

  const changeToEditor = useAsyncCallback(async () => {

    await updateUserRole(id, FolderRole.Write);
  }, [ updateUserRole, id]);

  const changeToReader = useAsyncCallback(async () => {

    await updateUserRole(id, FolderRole.Read);
  }, [updateUserRole, id]);


  const removeMember = useAsyncCallback(async () => {
    track.$.sharePanel.$.modifyUserDocRole({ control: 'Remove' });
    try {
      node?.removePermission({
        id,
        type,
        role: FolderRole.None,
      });
    } catch (error) {
      const err = UserFriendlyError.fromAny(error);
      notify.error({
        title: t[`error.${err.name}`](err.data),
      });
    }
  }, [ id, t]);

  const operationButtonInfo = useMemo(() => {
    return [
      {
        label: t['com.affine.share-menu.option.permission.can-manage'](),
        onClick: changeToManager,
        role: FolderRole.Admin,
      },
      {
        label: t['com.affine.share-menu.option.permission.can-edit'](),
        onClick: changeToEditor,
        role: FolderRole.Write,
      },
      {
        label: t['com.affine.share-menu.option.permission.can-read'](),
        onClick: changeToReader,
        role: FolderRole.Read,
      },
    ];
  }, [changeToEditor, changeToManager, changeToReader, t]);

  return (
    <>
      {operationButtonInfo.map(item => (
        <MenuItem
          key={item.label}
          onSelect={item.onClick}
          selected={memberRole === item.role}
          // disabled={!canManageUsers}
        >
          <div className={styles.planTagContainer}>
            {item.label}
          </div>
        </MenuItem>
      ))}
      <MenuSeparator />
      <MenuItem
        onSelect={removeMember}
        type="danger"
        className={styles.remove}
        disabled={!canDelete}
      >
        {t['com.affine.share-menu.member-management.remove']()}
      </MenuItem>
    </>
  );
};
